import {useState} from "react";
import {DAYS} from "../../../config/generals";
import {connect, ConnectedProps} from "react-redux";
import {RootState} from "../../../interfaces/root-state.interface";
import {setComparisonDate} from "../../../store/actions/dateRange.actions";
import {
    CustomDatePicker,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    Divider,
    Paper,
    Box,
    Collapse,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
    ExpandMore,
    ExpandLess
} from "@leap4/frontend";
import {FormLabel} from "@mui/material";
import {DateTime} from 'luxon';

const mapStateToProps = (state: RootState) => ({
    dates: state.dates,

});

const mapActions = {
    setComparisonDate
}

const connector = connect(mapStateToProps, mapActions);

type PropsFromRedux = ConnectedProps<typeof connector>;

export interface IMetricsComparisonProps extends PropsFromRedux {
    setSpecificComparisonDate?: any
    specificComparisonDate?: string
    optionalComparisonDates?: any
}

// Mobile abbreviated labels
const MOBILE_LABELS = {
    [DAYS.ONE.value]: '(-1)',
    [DAYS.SEVEN.value]: '(-7)',
    [DAYS.TWENTY_EIGHT.value]: '(-28)',
};

const _MetricsComparison = ({dates, setComparisonDate, specificComparisonDate, setSpecificComparisonDate, optionalComparisonDates}: IMetricsComparisonProps) => {
    const {selectedComparison} = dates;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [isDatePickerExpanded, setIsDatePickerExpanded] = useState(false);

    const handleChange = ({target}) => {
        const {value} = target;
        setComparisonDate(value)
        setSpecificComparisonDate(optionalComparisonDates[+value])
    }

    const handleChangeDatePicker = (val) =>{
        setSpecificComparisonDate(DateTime.fromJSDate(val).toUTC().plus({hour: 5}).startOf('day').toJSDate())
        setComparisonDate(-1)
    }

    // Get label based on screen size
    const getLabel = (day: typeof DAYS.ONE) => {
        return isMobile ? MOBILE_LABELS[day.value] : day.label;
    };

    // Format the specific date for display
    const formatSpecificDate = () => {
        if (!specificComparisonDate) return 'Select Date';
        return DateTime.fromJSDate(new Date(specificComparisonDate)).toFormat('MMM dd');
    };

    return <>
        <Paper elevation={5} style={{padding: '12px 16px', height:'100%', borderRadius: "16px",  boxShadow: "none"}}>

            <FormControl fullWidth disabled={dates.isLoading.isLoadingGeneral}>
                <FormLabel style={{marginBottom: '10px'}}>Comparison</FormLabel>
                <RadioGroup row onChange={handleChange} value={selectedComparison}>
                    <Stack width={"100%"} justifyContent={"center"} spacing={isMobile ? 0.5 : 1} 
                           direction="row"
                           alignItems={'center'}
                           flexWrap={isMobile ? 'wrap' : 'nowrap'}
                           divider={!isMobile ? <Divider orientation="vertical" flexItem /> : undefined}>
                        <FormControlLabel 
                            value={DAYS.ONE.value} 
                            control={<Radio size={isMobile ? 'small' : 'medium'} />} 
                            label={getLabel(DAYS.ONE)}
                            sx={isMobile ? { m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } } : {}}
                        />
                        <FormControlLabel 
                            value={DAYS.SEVEN.value} 
                            control={<Radio size={isMobile ? 'small' : 'medium'} />} 
                            label={getLabel(DAYS.SEVEN)}
                            sx={isMobile ? { m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } } : {}}
                        />
                        <FormControlLabel 
                            value={DAYS.TWENTY_EIGHT.value} 
                            control={<Radio size={isMobile ? 'small' : 'medium'} />}
                            label={getLabel(DAYS.TWENTY_EIGHT)}
                            sx={isMobile ? { m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } } : {}}
                        />
                        
                        {/* Specific Date - Collapsible on mobile, inline on desktop */}
                        {isMobile ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel 
                                    value={-1} 
                                    control={<Radio size="small" />}
                                    label=""
                                    sx={{ m: 0 }}
                                />
                                <Box 
                                    onClick={() => setIsDatePickerExpanded(!isDatePickerExpanded)}
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        cursor: 'pointer',
                                        bgcolor: selectedComparison === -1 ? 'action.selected' : 'action.hover',
                                        borderRadius: 1,
                                        px: 0.5,
                                        py: 0.25,
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                        {formatSpecificDate()}
                                    </Typography>
                                    <IconButton size="small" sx={{ p: 0, ml: 0.25 }}>
                                        {isDatePickerExpanded ? <ExpandLess sx={{ fontSize: '1rem' }} /> : <ExpandMore sx={{ fontSize: '1rem' }} />}
                                    </IconButton>
                                </Box>
                            </Box>
                        ) : (
                            <FormControlLabel 
                                style={{width: '150px'}} 
                                value={-1} 
                                control={<CustomDatePicker textFieldLabel={'Specific Date'} label={'Specific Date'} date={specificComparisonDate} setSelectedDate={handleChangeDatePicker} disableFuture={true} />}
                                label={""}
                            />
                        )}
                    </Stack>
                </RadioGroup>
                
                {/* Collapsible date picker for mobile */}
                {isMobile && (
                    <Collapse in={isDatePickerExpanded}>
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                            <CustomDatePicker 
                                textFieldLabel={'Specific Date'} 
                                label={'Specific Date'} 
                                date={specificComparisonDate} 
                                setSelectedDate={handleChangeDatePicker} 
                                disableFuture={true} 
                            />
                        </Box>
                    </Collapse>
                )}
            </FormControl>
        </Paper>
    </>
}

export const MetricsComparison = connector(_MetricsComparison)